import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getAlternativePrompt, getDashboard } from "../api/dashboard";
import { InkDivider } from "../components/InkDivider";
import { RisographBackground } from "../components/RisographBackground";
import { useAuth } from "../hooks/useAuth";
import type { HomeStory, Prompt } from "../types";
import { accentForId, accentGradient } from "../utils/accent";

function formatDate(d: Date, locale: string): string {
	return d.toLocaleDateString(locale, {
		weekday: "long",
		month: "long",
		day: "numeric",
	});
}

function formatShortDate(isoString: string, locale: string): string {
	const d = new Date(isoString);
	return d.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

const ENCOURAGEMENT_LINE_COUNT = 5;

function SectionLabel({ children }: { children: React.ReactNode }) {
	return (
		<p className="font-ui text-[13px] uppercase tracking-[0.2em] text-ink/55 mb-3">
			{children}
		</p>
	);
}

function UnfinishedBanner({
	stories,
	total,
}: {
	stories: HomeStory[];
	total: number;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const isSingle = total === 1;
	const label = isSingle
		? `"${stories[0]?.title ?? t("home.untitled")}"`
		: t("home.stillTellingMany", { count: total });

	function handleClick() {
		if (isSingle && stories[0]) {
			navigate(`/stories/${stories[0].id}`);
		} else {
			navigate("/stories");
		}
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			className="w-full flex items-center gap-3 bg-[#C4855A]/10 hover:bg-[#C4855A]/15 border border-[#C4855A]/20 rounded-sm px-4 py-3 mb-8 text-left transition-colors"
			style={{ borderLeft: "3px solid #C4855A" }}
		>
			<span className="font-ui text-[#C4855A] text-sm shrink-0">↩</span>
			<span className="flex-1 font-display text-sm text-ink truncate">
				{label}
			</span>
			<span className="font-ui text-[10px] uppercase tracking-[0.25em] text-[#C4855A] shrink-0">
				{t("home.continue")} →
			</span>
		</button>
	);
}

function StoryRow({ story, locale }: { story: HomeStory; locale: string }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const accent = accentForId(story.id);
	return (
		<button
			type="button"
			onClick={() => navigate(`/stories/${story.id}`)}
			className="w-full backdrop-blur-sm border border-ink/10 rounded-sm pl-5 pr-6 py-5 transition-all text-left hover:brightness-[1.03]"
			style={{
				borderLeft: `3px solid ${accent}`,
				background: accentGradient(accent),
			}}
		>
			<h3 className="font-display text-xl font-bold text-ink leading-tight">
				{story.title ?? t("home.untitled")}
			</h3>
			{story.preview && (
				<p className="font-body italic text-sm text-ink/60 mt-1.5 line-clamp-2 leading-relaxed">
					{story.preview}
				</p>
			)}
			<p className="font-ui text-[11px] uppercase tracking-widest text-ink/50 mt-1.5">
				{formatShortDate(story.created_at, locale)}
			</p>
		</button>
	);
}

function PromptCard({
	prompt,
	onRecord,
	onWrite,
	onDifferent,
	isLoadingAlternative,
}: {
	prompt: Prompt;
	onRecord: () => void;
	onWrite: () => void;
	onDifferent: () => void;
	isLoadingAlternative: boolean;
}) {
	const { t, i18n } = useTranslation();
	return (
		<div className="bg-white/30 backdrop-blur-sm border border-ink/10 rounded-sm p-5">
			<p className="font-ui text-[10px] tracking-[0.25em] text-[#C4855A]/80 uppercase mb-3">
				{formatDate(new Date(), i18n.language)}
			</p>
			<p className="font-display italic text-[17px] leading-[1.55] text-ink mb-5">
				"{prompt.content}"
			</p>
			<div className="flex gap-2">
				<button
					type="button"
					onClick={onRecord}
					className="ink-btn flex-1 gap-2 py-3 font-ui text-xs uppercase tracking-widest"
				>
					<span className="w-1.5 h-1.5 rounded-full bg-cream/60" />
					{t("home.record")}
				</button>
				<button
					type="button"
					onClick={onWrite}
					className="flex-1 py-3 border border-ink/25 rounded-sm font-ui text-xs uppercase tracking-widest text-ink/60 hover:border-ink/50 hover:text-ink/80 transition-colors"
				>
					{t("home.write")}
				</button>
			</div>
			<div className="flex justify-end mt-2.5">
				<button
					type="button"
					onClick={onDifferent}
					disabled={isLoadingAlternative}
					className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink/40 hover:text-ink/60 transition-colors disabled:opacity-30"
				>
					{t("home.anotherPrompt")} →
				</button>
			</div>
		</div>
	);
}

function EncouragementNudge({ count }: { count: number }) {
	const { t } = useTranslation();
	const weekNum = Math.floor(Date.now() / (1000 * 60 * 60 * 24 * 7));
	const lineKey =
		`home.encouragement.line${weekNum % ENCOURAGEMENT_LINE_COUNT}` as
			| "home.encouragement.line0"
			| "home.encouragement.line1"
			| "home.encouragement.line2"
			| "home.encouragement.line3"
			| "home.encouragement.line4";
	const line = t(lineKey);
	const text =
		count >= 3 ? t("home.encouragement.withCount", { count, line }) : line;

	return (
		<div className="bg-white/30 backdrop-blur-sm border border-ink/10 rounded-sm p-5 flex items-start gap-4">
			<span className="text-[#C4855A] text-xl mt-0.5 shrink-0">◎</span>
			<p className="font-body italic text-sm text-ink leading-relaxed">
				{text}
			</p>
		</div>
	);
}

export function HomePage() {
	const { t, i18n } = useTranslation();
	const { name } = useAuth();
	const navigate = useNavigate();
	const [currentPrompt, setCurrentPrompt] = useState<Prompt | null>(null);
	const [seenPromptIds, setSeenPromptIds] = useState<number[]>([]);
	const [isLoadingAlternative, setIsLoadingAlternative] = useState(false);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["dashboard"],
		queryFn: getDashboard,
		staleTime: 1000 * 60 * 5,
	});

	const activePrompt = currentPrompt ?? data?.today_prompt ?? null;

	function handleRecord() {
		if (!activePrompt) return;
		navigate("/stories/new", {
			state: { promptContext: activePrompt.content, inputMode: "voice" },
		});
	}

	function handleWrite() {
		if (!activePrompt) return;
		navigate("/stories/new", {
			state: { promptContext: activePrompt.content, inputMode: "text" },
		});
	}

	async function handleDifferentPrompt() {
		if (!activePrompt) return;
		const excludeIds =
			seenPromptIds.length > 0 ? seenPromptIds : [activePrompt.id];
		setIsLoadingAlternative(true);
		try {
			const next = await getAlternativePrompt(excludeIds);
			setCurrentPrompt(next);
			setSeenPromptIds((prev) => [...prev, next.id]);
		} finally {
			setIsLoadingAlternative(false);
		}
	}

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<RisographBackground />
				<p className="font-ui text-xs uppercase tracking-widest text-ink/30 animate-pulse">
					{t("home.loading")}
				</p>
			</div>
		);
	}

	if (isError || !data) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<RisographBackground />
				<p className="font-ui text-xs uppercase tracking-widest text-ink/40">
					{t("home.error")}
				</p>
			</div>
		);
	}

	const {
		ready_to_refine,
		ready_to_refine_total,
		recent_stories,
		total_stories,
		has_encouragement,
		week_story_count,
	} = data;
	const showViewAll = total_stories >= 4;

	return (
		<div className="min-h-screen">
			<RisographBackground />
			<main className="max-w-2xl mx-auto px-6 pt-8 pb-16">
				{/* Welcome header */}
				<div className="flex items-start justify-between mb-2">
					<h1 className="font-display text-5xl font-bold text-ink leading-tight">
						{t("home.welcome", { name: name?.split(" ")[0] ?? "back" })}
					</h1>
				</div>
				<InkDivider className="mb-8 mt-4" />

				{/* Unfinished stories banner */}
				{ready_to_refine_total > 0 && (
					<UnfinishedBanner
						stories={ready_to_refine}
						total={ready_to_refine_total}
					/>
				)}

				{/* Today's Prompt */}
				<section className="mb-8">
					<SectionLabel>{t("home.todaysPrompt")}</SectionLabel>
					{activePrompt && (
						<PromptCard
							prompt={activePrompt}
							onRecord={handleRecord}
							onWrite={handleWrite}
							onDifferent={handleDifferentPrompt}
							isLoadingAlternative={isLoadingAlternative}
						/>
					)}
					<div className="flex justify-end mt-3">
						<button
							type="button"
							onClick={() => navigate("/stories/new")}
							className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink/35 hover:text-ink/55 transition-colors"
						>
							{t("home.startWithoutPrompt")} →
						</button>
					</div>
				</section>

				<InkDivider className="mb-8" />

				{/* Recent Stories */}
				<section className="mb-8">
					<SectionLabel>{t("home.recentStories")}</SectionLabel>
					{recent_stories.length === 0 ? (
						<p className="font-body italic text-sm text-ink/40 text-center py-6">
							{t("home.storiesEmpty")}
						</p>
					) : (
						<>
							<div className="space-y-4">
								{recent_stories.map((story) => (
									<StoryRow
										key={story.id}
										story={story}
										locale={i18n.language}
									/>
								))}
							</div>
							{showViewAll && (
								<button
									type="button"
									onClick={() => navigate("/stories")}
									className="w-full flex justify-between items-center py-4 hover:bg-white/30 rounded-sm px-1 -mx-1 transition-colors"
								>
									<span className="font-ui text-[13px] text-ink/60">
										{t("home.viewAll")}
									</span>
									<span className="font-ui text-[10px] tracking-[0.25em] text-ink/40">
										{t("home.totalStories", { count: total_stories })}
									</span>
								</button>
							)}
						</>
					)}
				</section>

				{/* Encouragement Nudge */}
				{has_encouragement && (
					<>
						<InkDivider className="mb-8" />
						<EncouragementNudge count={week_story_count} />
					</>
				)}
			</main>
		</div>
	);
}
